import React, { ReactNode } from 'react';
import { Box, Typography } from '@heycater/design-system';
import { BoxProps } from '@material-ui/core';
import styled from 'styled-components';

import { useBreakpointDown } from 'qualification/hooks/useBreakpoints';
import { useStickyBottomSection } from 'shared/components/StickyBottomSection';

interface Props {
  answers: ReactNode;
  errors: ReactNode;
  nextButton: ReactNode;
  prevButton: ReactNode;
  footNote: ReactNode;
}

/**
 * Lays out the error messages, navigation buttons, and the answer pills
 */
export const SurveyControlsLayout: React.FC<Props> = ({
  answers,
  errors,
  nextButton,
  prevButton,
  footNote,
}) => {
  const isMobile = useBreakpointDown('xs');
  const isDesktop = !isMobile;

  const {
    ref: trackIfInViewportRef,
    isFloating,
    height: floatingSectionHeight,
  } = useStickyBottomSection();

  if (isMobile) {
    return (
      <Box
        display="flex"
        position="sticky"
        bottom="0"
        flexDirection="column"
        flexGrow={1}
      >
        <WhiteShadow mt="auto">
          {answers}
          {errors && (
            <Box pb={2} pt={1}>
              {errors}
            </Box>
          )}
          {(answers || errors) && <Divider />}
          <Box px={1.5} py={1.5}>
            <Box display="flex" as="nav">
              {prevButton}
              {nextButton && <Box ml="auto">{nextButton}</Box>}
            </Box>
            {footNote && (
              <Box display="flex" justifyContent="center" pt={0.5}>
                <Typography variant="caption">{footNote}</Typography>
              </Box>
            )}
          </Box>
        </WhiteShadow>
      </Box>
    );
  }

  if (isDesktop) {
    const ButtonsAndErrors = (
      <>
        {errors && <Box pt={2}>{errors}</Box>}
        <Box py={2} px={4}>
          <Box display="flex" as="nav" justifyContent="center">
            {prevButton}
            {nextButton && <Box ml={4}>{nextButton}</Box>}
          </Box>
          {footNote && (
            <Box display="flex" justifyContent="center" pt={0.5}>
              <Typography variant="caption">{footNote}</Typography>
            </Box>
          )}
        </Box>
      </>
    );

    return (
      <Box
        ref={trackIfInViewportRef}
        display="flex"
        flexDirection="column"
        flexGrow={1}
      >
        {ButtonsAndErrors}
        <StickyBottomSection
          height={floatingSectionHeight}
          isFloating={isFloating}
        >
          {isFloating ? (
            <WhiteShadow>
              <Divider />
              {answers}
            </WhiteShadow>
          ) : (
            <>
              <WhiteShadow mt="auto">{answers}</WhiteShadow>
            </>
          )}
        </StickyBottomSection>
      </Box>
    );
  }
  return null;
};

interface StickyBottomProps extends BoxProps {
  children: ReactNode;
  height: number;
  isFloating: boolean;
}

const StickyBottomSection = ({
  children,
  height,
  isFloating,
  ...restProps
}: StickyBottomProps) => {
  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        flexGrow="1"
        bottom="0"
        left="0"
        right="0"
        position={isFloating ? 'fixed' : 'relative'}
        mt="auto"
        {...restProps}
      >
        {children}
      </Box>

      {/* 
        Placeholder, to avoid layout shifts.
        so if the content starts floating, 
        it's original space is kept in the layout 
      */}
      {isFloating && <div style={{ height: `${height}px` }} />}
    </>
  );
};

const Divider = styled.div`
  height: 1px;
  background-color: ${(props) => props.theme.palette.grey[250]};
  width: 100%;
  position: absolute;
`;

const WhiteShadow = styled(Box)`
  background-color: ${(props) => props.theme.palette.common.white};
  box-shadow: 0 0 20px 0 rgb(0 0 0 / 12%);
`;
